import { useState, useEffect } from 'react'

// ── celebrationQueue ──────────────────────────────────────────────────────────
// One "stage" that celebration MOMENTS take turns on, so they never overlap. A
// moment acquires the stage, plays alone, and releases it only when dismissed
// (tap-away / close / its own timer) — then the next plays. This replaces the
// free-for-all where a Marquee, an achievement toast, and the rating prompt all
// fired at once and clobbered each other.
//
// Ordering: grants are batched within a tick and ordered by PRIORITY (then FIFO),
// so when several moments fire together the most important one plays first
// (trophy > achievement > rating prompt) instead of whichever effect happened to
// run first.
//
// Scope: this queue serializes the newer moments (Marquees, the post-mark rating
// prompt, achievement unlocks). The battle-tested finale chain (Wrap → Card →
// FinalCut) keeps its own internal coordination via window.__nextepWrapUntil; we
// BRIDGE to it both ways:
//   • the queue won't grant the stage while a finale cinematic is up (finaleBusy)
//   • while a queue moment is on stage we raise __nextepWrapUntil, so CardReveal
//     and the rating toast (which already honor that flag) wait their turn too.

export const STAGE_PRIORITY = { marquee: 100, achievement: 50, rating: 10 }

let queue = []          // [{ id, priority, seq, grant }]
let activeId = null
let seqCounter = 0
let slotUid = 0         // process-unique id source for useStageSlot (per-instance refs collided)
let pumpScheduled = false
let recheck = null
const subs = new Set()

function finaleBusy() {
  if (typeof window === 'undefined') return false
  // A finale cinematic is up if it flagged itself visible (CardReveal clears the
  // hold timestamp while showing, so the explicit flag is what holds the line),
  // OR the hold timestamp is still in the future AND it isn't our own raise.
  if (window.__nextepFinaleActive) return true
  // A modal that owns the screen (What's New takeover, a batch receipt) holds the
  // stage too — a Marquee played straight over the update sheet (audit 2026-10-11).
  if (window.__nextepModalHold) return true
  return !!(window.__nextepWrapUntil && window.__nextepWrapUntil > Date.now() && !window.__nextepStageOwnsHold)
}

function emit() { subs.forEach(fn => { try { fn(activeId) } catch {} }) }

function raiseFinaleHold() {
  // Keep the legacy flag in the near future while a queue moment is active so the
  // finale-chain overlays (CardReveal, the rating toast) defer to US. The owns-
  // hold marker tells finaleBusy() this hold is ours, not a real finale cinematic.
  if (typeof window !== 'undefined') {
    window.__nextepWrapUntil = Date.now() + 4000
    window.__nextepStageOwnsHold = true
  }
}

function pump() {
  if (activeId) { raiseFinaleHold(); return }
  if (finaleBusy()) { scheduleRecheck(); return }   // wait for Wrap/Card/FinalCut
  if (!queue.length) return
  // Highest priority first; FIFO within the same priority.
  queue.sort((a, b) => (b.priority - a.priority) || (a.seq - b.seq))
  const next = queue.shift()
  activeId = next.id
  raiseFinaleHold()
  next.grant()
  emit()
}

function schedulePump() {
  if (pumpScheduled) return
  pumpScheduled = true
  Promise.resolve().then(() => { pumpScheduled = false; pump() })
}

function scheduleRecheck() {
  if (recheck) return
  recheck = setTimeout(() => { recheck = null; pump() }, 350)
}

// Acquire the stage. Resolves when it's this moment's turn.
export function acquireStage(id, priority = 0) {
  return new Promise(resolve => {
    queue.push({ id, priority, seq: ++seqCounter, grant: resolve })
    schedulePump()
  })
}

// Release the stage (or cancel a still-queued request).
export function releaseStage(id) {
  if (activeId === id) {
    activeId = null
    if (typeof window !== 'undefined') {
      window.__nextepWrapUntil = 0
      window.__nextepStageOwnsHold = false
      // Wake the finale-chain overlays that were deferring to us.
      window.dispatchEvent(new CustomEvent('nextep:wrap-done'))
    }
    emit()
    schedulePump()
  } else {
    queue = queue.filter(q => q.id !== id)
  }
}


// When a finale cinematic finishes it dispatches wrap-done / card-done — that's
// our cue to try granting the next queued moment.
if (typeof window !== 'undefined') {
  window.addEventListener('nextep:wrap-done', () => { if (!activeId) schedulePump() })
  window.addEventListener('nextep:card-done', () => { if (!activeId) schedulePump() })
}

// ── React helper ──────────────────────────────────────────────────────────────
// useStageSlot(want, priority): when `want` flips true, request the stage;
// returns `granted` true once it's this component's turn. Releases automatically
// when `want` goes false (or on unmount).
export function useStageSlot(want, priority = 0) {
  const [granted, setGranted] = useState(false)
  useEffect(() => {
    if (!want) { setGranted(false); return }
    // Process-unique id — a per-instance ref counter produced id=1 in EVERY
    // component, so releaseStage(id) could free another component's active lock
    // mid-cinematic (audit 2026-07-22).
    const id = `slot-${++slotUid}`
    let live = true
    acquireStage(id, priority).then(() => { if (live) setGranted(true) })
    return () => { live = false; setGranted(false); releaseStage(id) }
  }, [want, priority])
  return granted
}
